'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  Building2,
  Users,
  ShieldCheck,
  CheckCircle2,
  Download,
  Send,
  Layers,
  Sparkles,
  ArrowRight,
  Briefcase,
  Gift,
  Award,
  Truck,
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import type { OccasionPackage } from '@/data/mockData';
import { fetchLiveHampers } from '@/services/api';
import { useCommerce } from '@/context/CommerceContext';


export default function CorporatePage() {
  const { openRfqWithData, hampers: contextHampers } = useCommerce();

  const initialCorporate = React.useMemo(() => {
    if (!contextHampers || contextHampers.length === 0) return [];
    return contextHampers.filter(
      (h) =>
        h.moduleType === 'corporate' ||
        ['corporate', 'executive', 'vip', 'corporate-diwali', 'employee-milestone', 'dealer-rewards', 'conferences'].includes(h.occasionCategory)
    );
  }, [contextHampers]);

  const [hampers, setHampers] = useState<OccasionPackage[]>(initialCorporate);
  const [loading, setLoading] = useState<boolean>(initialCorporate.length === 0);

  React.useEffect(() => {
    let isMounted = true;
    fetchLiveHampers('corporate')
      .then((data) => {
        if (isMounted) {
          setHampers(Array.isArray(data) ? data : []);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.warn('Failed to fetch corporate hampers from backend:', err);
        if (isMounted) {
          setHampers(initialCorporate || []);
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [initialCorporate]);

  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    estimatedBoxes: '50',
    targetBudget: '₹500 - ₹1,000 per box',
    customizationRequirements: 'Logo branding on box, custom greeting card',
  });

  const [submitted, setSubmitted] = useState(false);

  const handleInquire = (hamper: OccasionPackage) => {
    openRfqWithData({
      companyName: formData.companyName || 'Corporate Client',
      occasion: hamper.occasion || hamper.occasionCategory || 'Corporate Relations',
      packageTitle: hamper.title,
      orderType: 'CORPORATE',
      estimatedBoxes: hamper.minOrderQty || 20,
      notes: `Interested in ${hamper.title} (${hamper.packagingType}) with custom company logo.`,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    openRfqWithData({
      companyName: formData.companyName,
      clientName: formData.contactName,
      email: formData.email,
      phone: formData.phone,
      orderType: 'CORPORATE',
      estimatedQuantity: parseInt(formData.estimatedBoxes, 10) || 50,
      notes: `Budget: ${formData.targetBudget}. Customization: ${formData.customizationRequirements}`,
    });
    setSubmitted(true);
  };

  const capabilities = [
    {
      icon: Briefcase,
      title: 'Executive Client Hampers',
      subtitle: 'Luxury Relationship Gifting',
      desc: 'Rigid magnetic trunks, gold-embossed logo sleeves, and artisanal sweets for VIP clients.',
    },
    {
      icon: Users,
      title: 'Employee Festive Rewards',
      subtitle: 'Nationwide Home Courier',
      desc: 'Seamless spreadsheet upload and individual doorstep delivery to 10,000+ employee homes.',
    },
    {
      icon: Layers,
      title: 'Conferences & Summits',
      subtitle: 'Single-Serve Delegate Packs',
      desc: 'Mess-free portioned traditional snack boxes with synchronized event venue delivery.',
    },
    {
      icon: Award,
      title: 'Bespoke Brand Customization',
      subtitle: 'GST Invoices & Corporate Co-Branding',
      desc: 'Metallic foil logos, personalized leadership message parchment, and full GST compliance.',
    },
  ];

  return (
    <main className="min-h-screen flex flex-col bg-white">
      <Header />

      {/* 1. Hero Banner (Clean, refined aesthetic matching Occasions page) */}
      <section className="bg-[#FAFBF9] border-b border-neutral-200 py-12 sm:py-16">
        <div className="w-full px-4 sm:px-6 md:px-10 lg:px-12 xl:px-16 2xl:px-20 text-center space-y-4">
          <div className="inline-flex items-center space-x-1.5 text-xs font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100/70 px-3.5 py-1 rounded-full">
            <Building2 className="w-3.5 h-3.5 text-emerald-700" />
            <span>Corporate Food Gifting Division</span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-semibold text-neutral-900 uppercase tracking-tight">
            PREMIUM CORPORATE HAMPERS & GIFTS
          </h1>

          <p className="text-sm sm:text-base text-neutral-600 max-w-2xl mx-auto font-medium leading-relaxed">
            Delight clients, executives, and employee teams with authentic South Indian artisanal food hampers.
            Handcrafted in pure cold-pressed oil, luxury packaging, custom logo branding, and nationwide doorstep delivery.
          </p>

          <div className="pt-2 flex flex-wrap items-center justify-center gap-4">
            <a
              href="#corporate-hampers"
              className="px-6 py-3 rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-bold text-xs sm:text-sm uppercase tracking-wider transition-all shadow-md"
            >
              Explore Pre-Curated Hampers
            </a>
            <a
              href="#rfq-form"
              className="px-6 py-3 rounded-full bg-white border border-neutral-300 hover:border-emerald-700 text-neutral-800 hover:text-emerald-800 font-bold text-xs sm:text-sm uppercase tracking-wider transition-all"
            >
              Request Custom Proposal
            </a>
          </div>
        </div>
      </section>

      {/* 2. Capabilities & Solutions Grid */}
      <section className="py-12 bg-white border-b border-neutral-100">
        <div className="w-full px-4 sm:px-6 md:px-10 lg:px-12 xl:px-16 2xl:px-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {capabilities.map((c, i) => {
              const Icon = c.icon;
              return (
                <div
                  key={i}
                  className="p-5 rounded-2xl bg-[#FAFBF9] border border-neutral-200/80 hover:border-emerald-600/50 hover:shadow-md transition-all space-y-2"
                >
                  <div className="w-9 h-9 rounded-xl bg-emerald-100/70 text-emerald-800 flex items-center justify-center mb-2">
                    <Icon className="w-4 h-4 text-emerald-800" />
                  </div>
                  <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider block">
                    {c.subtitle}
                  </span>
                  <h3 className="text-base font-bold text-neutral-900">
                    {c.title}
                  </h3>
                  <p className="text-xs text-neutral-600 leading-relaxed font-medium">
                    {c.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3. Pre-Curated Corporate Hampers (Horizontal Cards with Details on Left, Image on Right) */}
      <section id="corporate-hampers" className="py-14 bg-white flex-1">
        <div className="w-full px-4 sm:px-6 md:px-10 lg:px-12 xl:px-16 2xl:px-20 space-y-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-neutral-900 uppercase tracking-tight">
                PRE-CURATED CORPORATE HAMPERS
              </h2>
              <div className="h-0.5 w-12 bg-emerald-600 rounded-full mt-1.5"></div>
            </div>
            <a
              href="#rfq-form"
              className="text-xs sm:text-sm font-bold text-emerald-700 hover:underline"
            >
              Need a Custom Hamper Mix? Get Quote →
            </a>
          </div>

          {loading && hampers.length === 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {[1, 2, 3, 4].map((n) => (
                <div
                  key={n}
                  className="rounded-3xl bg-neutral-50/80 border border-neutral-200/80 p-6 flex flex-col justify-between animate-pulse min-h-[320px]"
                >
                  <div className="space-y-3">
                    <div className="h-4 bg-neutral-200 rounded w-1/4"></div>
                    <div className="h-6 bg-neutral-200 rounded w-3/4"></div>
                    <div className="h-4 bg-neutral-200 rounded w-full"></div>
                    <div className="space-y-2 pt-4">
                      <div className="h-3.5 bg-neutral-200 rounded w-2/3"></div>
                      <div className="h-3.5 bg-neutral-200 rounded w-1/2"></div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-6 border-t border-neutral-200/60">
                    <div className="h-6 bg-neutral-200 rounded w-24"></div>
                    <div className="h-10 bg-neutral-200 rounded-xl w-32"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : hampers.length === 0 ? (
            <div className="text-center py-16 bg-[#FAFBF9] rounded-3xl border border-neutral-200/80 p-8 space-y-4">
              <Gift className="w-10 h-10 text-emerald-700 mx-auto" />
              <h3 className="text-lg font-bold text-neutral-800">No corporate hampers currently listed</h3>
              <p className="text-xs text-neutral-500 max-w-md mx-auto">
                Our gifting team can curate a bespoke hamper mix matching your exact company budget.
              </p>
              <a
                href="#rfq-form"
                className="inline-block px-5 py-2.5 rounded-full bg-emerald-800 text-white text-xs font-bold uppercase tracking-wider"
              >
                Request Custom Proposal
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {hampers.map((h) => (
                <div
                  key={h.id || h._id}
                  className="flex flex-col-reverse sm:flex-row items-stretch justify-between rounded-3xl bg-white border border-neutral-200/80 overflow-hidden hover:shadow-xl hover:border-emerald-600/40 transition-all duration-300 group"
                >
                  {/* LEFT SIDE: Details */}
                  <div className="flex-1 p-6 sm:p-7 flex flex-col justify-between space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider">
                          {h.occasion || h.occasionCategory || 'Corporate Gifting'}
                        </span>
                      </div>

                      <h3 className="text-lg sm:text-xl font-semibold text-neutral-900 leading-snug">
                        <Link href={`/corporate/${h.slug || h.id}`} className="hover:text-emerald-700 transition-colors">
                          {h.title}
                        </Link>
                      </h3>

                      <p className="text-xs text-neutral-600 mt-2 leading-relaxed font-medium">
                        {h.subtitle}
                      </p>
                    </div>

                    <div className="space-y-1.5 py-3 border-t border-b border-neutral-100">
                      <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider block">
                        Included Artisanal Delicacies &amp; Products:
                      </span>
                      <ul className="space-y-1.5 text-xs text-neutral-700 font-medium">
                        {(h.includedProducts && h.includedProducts.length > 0
                          ? h.includedProducts.map((p: any) => `${p.quantity > 1 ? `${p.quantity}x ` : ''}${p.name} (${p.weight || 'Standard'})`)
                          : (h.includedSnacks || [])
                        ).slice(0, 4).map((s, i) => (
                          <li key={i} className="flex items-center space-x-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                            <span className="line-clamp-1">{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="pt-1 flex items-center justify-between gap-3">
                      <div>
                        <span className="text-lg font-bold text-neutral-900">From ₹{h.basePrice}</span>
                        <span className="text-[10px] text-neutral-400 block font-medium">Min. {h.minOrderQty} hampers</span>
                      </div>

                      <Link
                        href={`/corporate/${h.slug || h.id}`}
                        className="py-2.5 px-4 rounded-xl bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold uppercase tracking-wider transition-colors cursor-pointer shadow-xs whitespace-nowrap"
                      >
                        Customize &amp; Quote →
                      </Link>
                    </div>
                  </div>

                  {/* RIGHT SIDE: Image */}
                  <Link
                    href={`/corporate/${h.slug || h.id}`}
                    className="block relative w-full sm:w-[42%] md:w-[44%] min-h-[260px] sm:min-h-[340px] bg-neutral-100 overflow-hidden shrink-0"
                  >
                    <Image
                      src={h.image || '/images/royal_gift_box.jpg'}
                      alt={h.title || 'Corporate Hamper'}
                      fill
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 30vw"
                      unoptimized
                      className="object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-3.5 right-3.5 text-[10px] sm:text-[11px] font-bold text-white bg-emerald-800/90 backdrop-blur-xs px-3 py-1 rounded-full uppercase tracking-wider shadow-sm">
                      {h.badge}
                    </span>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* 4. Instant Corporate RFQ Form */}
      <section id="rfq-form" className="py-14 bg-[#FAFBF9] border-t border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 sm:p-10 rounded-3xl border border-neutral-200/90 shadow-md space-y-6">
            <div className="text-center space-y-2">
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">
                OFFICIAL B2B PROPOSAL
              </span>
              <h3 className="text-2xl font-semibold text-neutral-900 uppercase tracking-tight">
                Request Corporate Gifting Proposal
              </h3>
              <p className="text-xs text-neutral-500 font-medium">
                Our corporate relations desk responds with tax-compliant GST estimates and digital packaging proofs within 4 business hours.
              </p>
            </div>

            {submitted ? (
              <div className="p-6 rounded-2xl bg-emerald-50 border border-emerald-200 text-center space-y-2">
                <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
                <h4 className="text-base font-bold text-emerald-900">Proposal Request Received!</h4>
                <p className="text-xs text-emerald-700">
                  Our corporate manager has opened your RFQ and will email you the formal quotation with GST and branding options shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Company / Organization *</label>
                    <input
                      type="text"
                      required
                      value={formData.companyName}
                      onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                      placeholder="e.g. Infosys, Zoho, Tata"
                      className="w-full px-3.5 py-2.5 text-xs bg-neutral-50 border border-neutral-200 rounded-xl focus:outline-none focus:border-emerald-600 focus:bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Contact Person Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.contactName}
                      onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                      placeholder="Your full name"
                      className="w-full px-3.5 py-2.5 text-xs bg-neutral-50 border border-neutral-200 rounded-xl focus:outline-none focus:border-emerald-600 focus:bg-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Corporate Email Address *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="name@company.com"
                      className="w-full px-3.5 py-2.5 text-xs bg-neutral-50 border border-neutral-200 rounded-xl focus:outline-none focus:border-emerald-600 focus:bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Mobile / WhatsApp Number *</label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+91 98765 43210"
                      className="w-full px-3.5 py-2.5 text-xs bg-neutral-50 border border-neutral-200 rounded-xl focus:outline-none focus:border-emerald-600 focus:bg-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Estimated Quantity (Hampers)</label>
                    <input
                      type="number"
                      min="10"
                      value={formData.estimatedBoxes}
                      onChange={(e) => setFormData({ ...formData, estimatedBoxes: e.target.value })}
                      className="w-full px-3.5 py-2.5 text-xs bg-neutral-50 border border-neutral-200 rounded-xl focus:outline-none focus:border-emerald-600 focus:bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Target Budget Per Hamper</label>
                    <select
                      value={formData.targetBudget}
                      onChange={(e) => setFormData({ ...formData, targetBudget: e.target.value })}
                      className="w-full px-3.5 py-2.5 text-xs bg-neutral-50 border border-neutral-200 rounded-xl focus:outline-none focus:border-emerald-600 focus:bg-white"
                    >
                      <option>Under ₹500 per hamper</option>
                      <option>₹500 - ₹1,000 per hamper</option>
                      <option>₹1,000 - ₹2,000 per hamper</option>
                      <option>Luxury Executive (₹2,000+)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Branding & Delivery Notes</label>
                  <textarea
                    rows={3}
                    value={formData.customizationRequirements}
                    onChange={(e) => setFormData({ ...formData, customizationRequirements: e.target.value })}
                    placeholder="Mention custom logo engraving, individual employee doorstep addresses, or preferred delivery dates..."
                    className="w-full px-3.5 py-2.5 text-xs bg-neutral-50 border border-neutral-200 rounded-xl focus:outline-none focus:border-emerald-600 focus:bg-white"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-3.5 rounded-xl bg-emerald-800 hover:bg-emerald-900 text-white font-bold text-xs uppercase tracking-wider shadow-md transition-all cursor-pointer flex items-center justify-center space-x-2"
                >
                  <Send className="w-4 h-4" />
                  <span>Submit Corporate RFQ</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* 5. Bottom Corporate CTA Banner */}
      <section className="bg-[#0A261A] text-white py-14">
        <div className="w-full px-4 sm:px-6 md:px-10 lg:px-12 xl:px-16 2xl:px-20 text-center space-y-4">
          <h2 className="text-2xl sm:text-4xl font-semibold uppercase tracking-tight">
            Planning Gifting for 500+ Employees or VIP Clients?
          </h2>
          <p className="text-xs sm:text-sm text-neutral-300 max-w-xl mx-auto font-medium leading-relaxed">
            Connect directly with our Chief Experience Officer for volume discounts, customized tasting kits, and dedicated dispatch managers.
          </p>
          <div className="pt-2 flex flex-wrap items-center justify-center gap-4">
            <a
              href="https://wa.me/919443210890?text=Hi%2C%20I%20would%20like%20to%20inquire%20about%20Corporate%20Food%20Hampers"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 rounded-full bg-[#F5B52E] hover:bg-[#ffc34d] text-[#0A261A] font-bold text-xs sm:text-sm uppercase tracking-wider transition-all"
            >
              WhatsApp Corporate Desk
            </a>
            <Link
              href="/contact"
              className="px-6 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold text-xs sm:text-sm uppercase tracking-wider transition-all"
            >
              Contact Commercial Team
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
